import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { HelpInfoCallout } from '@/components/config_forms/short_turn/components/HelpInfoCallout';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  TimelineBar,
  TimeAxis,
  type TimelineZone,
  type TimeAxisTick,
} from '@/components/config_forms/shared/help-graphics/timeline/TimelinePrimitives';
import {
  TIMELINE_ANNOTATION_INLINE_CLASS,
} from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';
import { RestGapConnector } from '@/components/config_forms/shared/help-graphics/RestGapConnector';
import { CONTROL_ROW_BODY_CLASS, CONTROL_ROW_LABEL_CLASS } from '../ControlRow';
import { SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING } from '../help-graphics/helpSheetOpticalSpacing';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface SurchargeScopeComparisonProps {
  shiftHours: number;
  shortfallHours: number;
  /** Current config value — controls which mode is active */
  activeMode?: ScopeMode;
  /** Called when the user toggles the mode — should update the config */
  onModeChange?: (mode: ScopeMode) => void;
}

type ScopeMode = 'full_shift' | 'violating_hours';

/* ------------------------------------------------------------------ */
/*  Zone content helpers                                               */
/* ------------------------------------------------------------------ */

const SurchargeLabel: React.FC = () => (
  <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px]" style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 8%)' }}>
    Surcharge
  </span>
);

const NoSurchargeLabel: React.FC = () => (
  <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px]" style={{ color: 'color-mix(in srgb, var(--color-foreground), transparent 55%)' }}>
    No Surcharge
  </span>
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export const SurchargeScopeComparison: React.FC<SurchargeScopeComparisonProps> = ({
  shiftHours,
  shortfallHours,
  activeMode: controlledMode,
  onModeChange,
}) => {
  const [internalMode, setInternalMode] = useState<ScopeMode>('violating_hours');
  const [useCenteredDesktopLayout, setUseCenteredDesktopLayout] = useState(false);
  const layoutRowRef = React.useRef<HTMLDivElement | null>(null);
  const controlBlockRef = React.useRef<HTMLDivElement | null>(null);
  const measureBlockRef = React.useRef<HTMLDivElement | null>(null);
  const measureRafRef = React.useRef<number | null>(null);
  const activeMode = controlledMode ?? internalMode;
  const setActiveMode = (mode: ScopeMode) => {
    if (onModeChange) onModeChange(mode);
    else setInternalMode(mode);
  };

  const isFullShift = activeMode === 'full_shift';

  /* ---- zones ---- */
  // Use a minimum flex so the surcharge zone is never too narrow
  const minSurchargeFlex = Math.max(shortfallHours, shiftHours * 0.35);
  const violatingNormalHours = shiftHours - shortfallHours;

  // Always render both zones so CSS transitions animate width/opacity changes.
  // In full_shift mode the normal zone collapses to flex:0 (width 0%, opacity 0).
  const zones: TimelineZone[] = [
    {
      id: 'surcharge',
      variant: 'v1',
      flex: isFullShift ? shiftHours : minSurchargeFlex,
      content: <SurchargeLabel />,
      isDeadlineBoundary: true,
    },
    {
      id: 'normal',
      variant: 'safe',
      flex: isFullShift ? 0 : violatingNormalHours,
      content: <NoSurchargeLabel />,
    },
  ];

  /* ---- ticks ---- */
  // Compute boundary position using violating-mode values so the tick stays
  // at its target position and only fades in/out via opacity.
  const boundaryPct = shiftHours > 0
    ? (minSurchargeFlex / (minSurchargeFlex + violatingNormalHours)) * 100
    : 0;
  const ticks: TimeAxisTick[] = [
    { id: 'start', label: 'shift\nstart', leftPct: 0, align: 'left', variant: 'label' },
    { id: 'boundary', label: `${shortfallHours}h\nshort`, leftPct: boundaryPct, align: 'center', variant: 'red', opacity: isFullShift ? 0 : 1 },
    { id: 'end', label: 'shift\nend', leftPct: 100, align: 'right', variant: 'label' },
  ];

  React.useEffect(() => {
    if (!onModeChange) return;

    const evaluateLayout = () => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth < 640) {
        setUseCenteredDesktopLayout(false);
        return;
      }

      const rowEl = layoutRowRef.current;
      const controlEl = controlBlockRef.current;
      const measureEl = measureBlockRef.current;
      if (!rowEl || !controlEl || !measureEl) return;

      const rowWidth = rowEl.clientWidth;
      const controlWidth = controlEl.offsetWidth;
      const controlHeight = controlEl.offsetHeight;
      if (rowWidth <= 0 || controlWidth <= 0 || controlHeight <= 0) return;

      // Simulate the wrapped text column width when the control is floated right.
      const constrainedTextWidth = Math.max(140, rowWidth - controlWidth - 16);
      measureEl.style.width = `${constrainedTextWidth}px`;
      const textHeight = measureEl.offsetHeight;

      // Keep flow layout when:
      // 1) the wrap column is too narrow for a comfortable side-by-side center layout, or
      // 2) wrapped text clears the control by a meaningful amount.
      // Otherwise use centered layout to remove dead space under the control.
      const overflowPastControl = textHeight - controlHeight;
      const minimumClearanceToKeepFlow = 40;
      const minimumWrapColumnWidthToAllowCentering = 158;
      const canUseCenteredDesktopLayout = (
        constrainedTextWidth >= minimumWrapColumnWidthToAllowCentering
      );
      const shouldUseFlowLayout = (
        !canUseCenteredDesktopLayout
        || overflowPastControl >= minimumClearanceToKeepFlow
      );
      setUseCenteredDesktopLayout(!shouldUseFlowLayout);
    };

    const queueEvaluate = () => {
      if (measureRafRef.current != null) {
        cancelAnimationFrame(measureRafRef.current);
      }
      measureRafRef.current = requestAnimationFrame(evaluateLayout);
    };

    queueEvaluate();
    window.addEventListener('resize', queueEvaluate);

    return () => {
      window.removeEventListener('resize', queueEvaluate);
      if (measureRafRef.current != null) {
        cancelAnimationFrame(measureRafRef.current);
      }
    };
  }, [onModeChange, activeMode, shortfallHours, shiftHours]);

  return (
    <div className="mb-0">
      {/* Rest connector */}
      <RestGapConnector
        hours={shortfallHours}
        triggered
        labelText={`${shortfallHours}hr Shorted`}
        className="pt-0 pb-2"
      />

      {/* Timeline */}
      <TimelineBar zones={zones} />
      <div className="mt-1">
        <TimeAxis ticks={ticks} />
      </div>

      {/* Annotation */}
      <p
        className={cn(
          TIMELINE_ANNOTATION_INLINE_CLASS,
          'motion-safe:transition-[margin-top,padding-bottom] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]',
          isFullShift
            ? 'mt-0 sm:-mt-6'
            : 'mt-1',
        )}
      >
        {isFullShift
          ? 'Every hour of the shift is billed at the surcharge rate.'
          : `Only the hours you were shorted are upgraded.`}
      </p>

      {onModeChange && (
        <>
          <HelpInfoCallout
            title="Important"
            className={SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.surchargePeriod}
          >
            This setting only applies to surcharge methods that augment the hourly rate.
            Fixed premium methods use a flat surcharge regardless.
          </HelpInfoCallout>
          <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
            <div ref={layoutRowRef} className={cn('relative px-3 py-2', useCenteredDesktopLayout && 'sm:flex sm:items-center sm:gap-4')}>
              {/* Invisible measurement node for wrap-vs-center decision */}
              <div
                ref={measureBlockRef}
                className="invisible pointer-events-none absolute left-0 top-0 -z-10"
                aria-hidden="true"
              >
                <span className={CONTROL_ROW_LABEL_CLASS}>
                  Surcharge Period
                </span>
                <p className={CONTROL_ROW_BODY_CLASS}>
                  Apply the surcharge to the entire shift or only the hours short of the rest requirement.
                </p>
              </div>
              {!useCenteredDesktopLayout && (
                <div
                  ref={controlBlockRef}
                  className="hidden mb-2 sm:mb-2 sm:float-right sm:ml-4 sm:min-w-[220px] sm:w-auto sm:block"
                >
                  <SegmentedControl
                    ariaLabel="Surcharge Period"
                    options={[
                      { value: 'violating_hours', label: 'Shorted Hours' },
                      { value: 'full_shift', label: 'Full Shift' },
                    ]}
                    value={activeMode ?? 'full_shift'}
                    onChange={(v) => setActiveMode(v as ScopeMode)}
                    className="w-full sm:w-auto sm:min-w-[220px]"
                    size="sm"
                  />
                </div>
              )}
              <div className={cn('min-w-0', useCenteredDesktopLayout && 'sm:flex-1')}>
                <span className={CONTROL_ROW_LABEL_CLASS}>
                  Surcharge Period
                </span>
                <p className={CONTROL_ROW_BODY_CLASS}>
                  Apply the surcharge to the entire shift or only the hours short of the rest requirement.
                </p>
              </div>
              {!useCenteredDesktopLayout && (
                <div className="mt-3 sm:hidden">
                  <SegmentedControl
                    ariaLabel="Surcharge Period"
                    options={[
                      { value: 'violating_hours', label: 'Shorted Hours' },
                      { value: 'full_shift', label: 'Full Shift' },
                    ]}
                    value={activeMode ?? 'full_shift'}
                    onChange={(v) => setActiveMode(v as ScopeMode)}
                    className="w-full"
                    size="sm"
                  />
                </div>
              )}
              {useCenteredDesktopLayout && (
                <div ref={controlBlockRef} className="mt-3 sm:mt-0 sm:shrink-0 sm:min-w-[220px]">
                  <SegmentedControl
                    ariaLabel="Surcharge Period"
                    options={[
                      { value: 'violating_hours', label: 'Shorted Hours' },
                      { value: 'full_shift', label: 'Full Shift' },
                    ]}
                    value={activeMode ?? 'full_shift'}
                    onChange={(v) => setActiveMode(v as ScopeMode)}
                    className="w-full sm:w-auto sm:min-w-[220px]"
                    size="sm"
                  />
                </div>
              )}
              {!useCenteredDesktopLayout && <div className="clear-both" />}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
